/**
 * StatsTab Component
 * 
 * Player statistics tab for match editing.
 * Shows goals, assists, cards, minutes, and POTM.
 */
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Target, Trophy, Coins } from 'lucide-react';

/**
 * @param {Object} props
 * @param {Object[]} props.playerStats - Array of player stat objects
 * @param {Object[]} props.players - Array of player objects
 * @param {boolean} props.isViewOnly - Whether stats are read-only
 * @param {Function} props.onStatChange - Handler for stat changes (playerId, field, value)
 */
export function StatsTab({
  playerStats = [],
  players = [],
  isViewOnly = false,
  onStatChange
}) {
  const getPlayerById = (playerId) => {
    return players.find(p => p.id === playerId);
  };

  if (playerStats.length === 0) {
    return (
      <Card className="rounded-2xl" data-testid="stats-tab-content">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Player Statistics</h3>
          <div className="text-center py-12 text-gray-400">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            <p>No players in squad</p>
            {!isViewOnly && <p className="text-sm mt-1">Add players to the squad first</p>}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl" data-testid="stats-tab-content">
      <CardContent className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Player Statistics</h3>
        
        <div className="space-y-3">
          {playerStats.map((stat) => {
            const player = getPlayerById(stat.player_id);
            if (!player) return null;
            
            return (
              <div key={stat.player_id} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                      {player.jersey_number || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{player.name}</p>
                      <p className="text-xs text-gray-500">
                        {stat.is_starter ? 'Starter' : 'Substitute'}
                      </p>
                    </div>
                  </div>
                  
                  {/* POTM Badge - shown when player is POTM */}
                  {stat.is_pom && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                      <Trophy className="w-3 h-3" />
                      <span className="text-xs font-medium">POTM</span>
                    </div>
                  )}
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-5 gap-2 mb-4">
                  <StatInput
                    label="Goals"
                    value={stat.goals}
                    isViewOnly={isViewOnly}
                    onChange={(value) => onStatChange?.(stat.player_id, 'goals', value)}
                  />
                  <StatInput
                    label="Assists"
                    value={stat.assists}
                    isViewOnly={isViewOnly}
                    onChange={(value) => onStatChange?.(stat.player_id, 'assists', value)}
                  />
                  <StatInput
                    label="Yellow"
                    value={stat.yellow_cards}
                    isViewOnly={isViewOnly}
                    onChange={(value) => onStatChange?.(stat.player_id, 'yellow_cards', value)}
                  />
                  <StatInput
                    label="Red"
                    value={stat.red_cards}
                    isViewOnly={isViewOnly}
                    onChange={(value) => onStatChange?.(stat.player_id, 'red_cards', value)}
                  />
                  <StatInput
                    label="Mins"
                    value={stat.minutes_played}
                    max={120}
                    isViewOnly={isViewOnly}
                    onChange={(value) => onStatChange?.(stat.player_id, 'minutes_played', value)}
                  />
                </div>

                {/* Toggle Section */}
                <div className="flex items-center justify-end pt-3 border-t border-gray-200">
                  {/* POTM Toggle */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-gray-600">POTM</span>
                    </div>
                    {isViewOnly ? (
                      <span className={`text-xs px-2 py-1 rounded-full ${stat.is_pom ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                        {stat.is_pom ? 'Yes' : 'No'}
                      </span>
                    ) : (
                      <Switch
                        checked={stat.is_pom || false}
                        onCheckedChange={(checked) => onStatChange?.(stat.player_id, 'is_pom', checked)}
                        data-testid={`potm-toggle-${stat.player_id}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * StatInput - Individual stat input field
 */
function StatInput({ label, value, max, isViewOnly, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      {isViewOnly ? (
        <div className="rounded-lg h-9 bg-white border border-gray-200 flex items-center justify-center text-sm font-medium text-gray-900">
          {value}
        </div>
      ) : (
        <Input
          type="number"
          min="0"
          max={max}
          value={value}
          onChange={(e) => onChange?.(parseInt(e.target.value) || 0)}
          className="rounded-lg h-9 text-center"
        />
      )}
    </div>
  );
}

export default StatsTab;
